import { createRef } from "react";
import { Button, Spinner } from "react-bootstrap";
import { X } from "react-bootstrap-icons";
import NumberFormat from "react-number-format";
import ChangeHighlight from "react-change-highlight";
import BlockyHeavyText from "components/custom-texts/blocky-heavy-text";
import BlockyBoldText from "components/custom-texts/blocky-bold-text";
import BlockyMediumText from "components/custom-texts/blocky-medium-text";
import { secondaryColor } from "utils/constants";
import styles from "assets/shared-stylesheets/sparket-list-item.module.css";
import highlightStyles from "components/pool-details/bet-modal/bet-modal-content/styles.module.css";
import BackCardShell from "./BackCardShell";
import { BackCardProps } from "./types";
import { CardHeader, Disclaimer } from "./SharedParts";
import popStyles from "./pop.module.css";

/**
 * Team review iteration 2. Built on iteration 1, with feedback:
 *  - More padding around the picks-left value
 *  - Baseline alignment on Odds / Exp. Return so the number sits flush with
 *    the label
 *  - Win Condition / Balance / Odds / Picks Left / Exp. Return labels all
 *    use the same gray font (poolCloseDatePrefix)
 *  - Remove the extra space after "Win Condition:"
 */
const VariantTeamPickV2 = (props: BackCardProps) => {
  const {
    eventName,
    poolName,
    winCondition,
    contestantName,
    isBasic,
    picksRemaining,
    expReturn,
    balanceLabel,
    balanceValue,
    oddsLabel,
    betAmount,
    onBetAmountChange,
    onConfirm,
    onCancel,
    loading,
    disabled,
  } = props;

  const valueStyle = {
    color: "var(--text-color)",
    fontWeight: 800 as const,
  };

  const grayLabelClass = styles.poolCloseDatePrefix;

  // Just X to close — kept from baseline. (CardHeader already provides it,
  // but pulled in directly to make V2 standalone if SharedParts ever
  // changes.)
  void X;

  return (
    <BackCardShell>
      <CardHeader
        eventName={eventName}
        poolName={poolName}
        onCancel={onCancel}
      />

      {/* Win Condition — single space between label and value */}
      <div>
        <BlockyHeavyText
          className={grayLabelClass}
          style={{ marginRight: 0 }}
        >
          Win Condition:{" "}
        </BlockyHeavyText>
        <BlockyMediumText
          style={{ fontSize: 12, display: "inline", lineHeight: 0.5 }}
        >
          {winCondition}
        </BlockyMediumText>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <BlockyHeavyText
            className={popStyles.pop}
            style={{
              fontSize: 16,
              ...valueStyle,
              wordBreak: "break-word",
            }}
          >
            {contestantName}
          </BlockyHeavyText>
        </div>
        {isBasic ? (
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 8,
              paddingRight: 12,
            }}
          >
            <BlockyHeavyText className={grayLabelClass}>
              Picks Left:
            </BlockyHeavyText>
            <BlockyBoldText
              className={popStyles.pop}
              style={{
                fontSize: 16,
                ...valueStyle,
                lineHeight: 1,
              }}
            >
              {picksRemaining ?? 0}
            </BlockyBoldText>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 110,
              paddingRight: 12,
            }}
          >
            <BlockyHeavyText
              className={grayLabelClass}
              style={{ textAlign: "center", marginRight: 0 }}
            >
              {balanceLabel || "Balance"}
            </BlockyHeavyText>
            <BlockyBoldText
              className={popStyles.pop}
              style={{
                fontSize: 16,
                ...valueStyle,
                lineHeight: 1.1,
                textAlign: "center",
              }}
            >
              {balanceValue}
            </BlockyBoldText>
          </div>
        )}
      </div>

      <div>
        {isBasic ? (
          <div style={{ padding: 3 }}>
            <Button
              style={{
                width: "100%",
                height: 41,
                backgroundColor: secondaryColor,
                color: "black",
              }}
              disabled={loading || disabled}
              onClick={onConfirm}
            >
              {loading && (
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ marginRight: 10 }}
                />
              )}
              Confirm
            </Button>
            <div
              style={{
                marginTop: 7,
                display: "flex",
                alignItems: "baseline",
              }}
            >
              <BlockyHeavyText className={grayLabelClass}>
                Exp. Return:
              </BlockyHeavyText>
              <ChangeHighlight
                hideAfter={1000}
                highlightClassName={highlightStyles.highlight}
              >
                <span
                  ref={createRef()}
                  className={popStyles.pop}
                  style={{ ...valueStyle, fontSize: 16, lineHeight: 1 }}
                >
                  {expReturn}
                </span>
              </ChangeHighlight>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", padding: 3 }}>
            <div style={{ flex: 0.5 }}>
              <NumberFormat
                value={betAmount}
                onValueChange={(v) => onBetAmountChange?.(v.formattedValue)}
                prefix="§"
                decimalScale={2}
                thousandSeparator
                inputMode="decimal"
                fixedDecimalScale
                allowNegative={false}
                placeholder="Enter Amount"
                style={{
                  borderRadius: 5,
                  height: 40,
                  color: "black",
                  width: "95%",
                  fontSize: 16,
                }}
              />
              <div
                style={{
                  marginTop: 7,
                  display: "flex",
                  alignItems: "baseline",
                }}
              >
                <BlockyHeavyText className={grayLabelClass}>
                  Odds:
                </BlockyHeavyText>
                <ChangeHighlight
                  hideAfter={1000}
                  highlightClassName={highlightStyles.highlight}
                >
                  <div
                    ref={createRef()}
                    className={popStyles.pop}
                    style={{ ...valueStyle, fontSize: 16, lineHeight: 1 }}
                  >
                    {oddsLabel}
                  </div>
                </ChangeHighlight>
              </div>
            </div>
            <div style={{ width: "100%", flex: 0.5 }}>
              <Button
                style={{
                  width: "100%",
                  height: 41,
                  backgroundColor: secondaryColor,
                  color: "black",
                }}
                disabled={loading || disabled}
                onClick={onConfirm}
              >
                {loading && (
                  <Spinner
                    animation="border"
                    size="sm"
                    style={{ marginRight: 10 }}
                  />
                )}
                Confirm
              </Button>
              <div
                style={{
                  marginTop: 7,
                  display: "flex",
                  alignItems: "baseline",
                }}
              >
                <BlockyHeavyText className={grayLabelClass}>
                  Exp. Return:
                </BlockyHeavyText>
                <ChangeHighlight
                  hideAfter={1000}
                  highlightClassName={highlightStyles.highlight}
                >
                  <span
                    ref={createRef()}
                    className={popStyles.pop}
                    style={{ ...valueStyle, fontSize: 16, lineHeight: 1 }}
                  >
                    {expReturn}
                  </span>
                </ChangeHighlight>
              </div>
            </div>
          </div>
        )}
      </div>
      <Disclaimer />
    </BackCardShell>
  );
};

export default VariantTeamPickV2;
